import { api } from "@/lib/axios";
import { handleApiError } from "@/lib/apiErrors";
import type {
  ProfileUpdateRequest,
  UploadQuota,
  User,
} from "@/lib/humhub-api/types";

interface ImageUploadResponse {
  success: boolean;
  image: {
    url: string;
    user_id: number;
  };
}

export const normalizeUser = <T extends User>(user: T): T => {
  return {
    ...user,
    bannerImageUrl: user.banner_image_url,
  };
};

export const usersApi = {
  getUserById: async (userId: number): Promise<User> => {
    try {
      const response = await api.get<User>(`user/${userId}`);

      if (!response.data) {
        throw new Error("Empty response from user API");
      }

      return normalizeUser(response.data);
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage:
          "Unexpected error occurred while fetching user information",
        label: "User API Error",
      });
    }
  },

  getUserByGuid: async (guid: string): Promise<User> => {
    try {
      const response = await api.get<User>(`user/guid/${guid}`);

      if (!response.data) {
        throw new Error("Empty response from user API");
      }

      return normalizeUser(response.data);
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage:
          "Unexpected error occurred while fetching user information",
        label: "User API Error",
      });
    }
  },

  updateUserProfile: async (
    updateData: ProfileUpdateRequest,
  ): Promise<User> => {
    try {
      const response = await api.put<User>(
        "user/update-own-settings",
        updateData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      return normalizeUser(response.data);
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage: "Unexpected error occurred while updating user profile",
        label: "Update user profile API Error",
        logResponseDetails: true,
      });
    }
  },
  // プロフィール画像アップロード
  uploadProfileImage: async (file: File): Promise<ImageUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await api.post<ImageUploadResponse>(
        "user/upload-profile-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!response.data.success) {
        throw new Error("Upload failed");
      }

      return response.data;
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage:
          "Unexpected error occurred while uploading profile image",
        label: "Upload profile image API Error",
      });
    }
  },

  // バナー画像アップロード
  uploadBannerImage: async (file: File): Promise<ImageUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("bannerImage", file);

      const response = await api.post<ImageUploadResponse>(
        "user/upload-banner-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!response.data.success) {
        throw new Error("Upload failed");
      }

      return response.data;
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage:
          "Unexpected error occurred while uploading banner image",
        label: "Upload banner image API Error",
      });
    }
  },

  blockUser: async (userId: number): Promise<boolean> => {
    try {
      const response = await api.post<{ success: boolean }>(
        `user/block/${userId}`,
      );

      if (!response.data) {
        throw new Error("Empty or invalid response from user API");
      }

      return response.data.success;
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage: "Unexpected error occurred while blocking the user",
        label: "Block user API Error",
      });
    }
  },

  unblockUser: async (userId: number): Promise<boolean> => {
    try {
      const response = await api.post<{ success: boolean }>(
        `user/unblock/${userId}`,
      );

      if (!response.data) {
        throw new Error("Empty or invalid response from user API");
      }

      return response.data.success;
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage: "Unexpected error occurred while unblocking the user",
        label: "Unblock user API Error",
      });
    }
  },

  blockedUsersList: async (): Promise<string[]> => {
    try {
      const response = await api.get<{ blocked_user_ids: string[] }>(
        "user/blocked-users",
      );
      if (!response.data) {
        throw new Error("Empty response from blocked users API");
      }
      return response.data.blocked_user_ids || [];
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage:
          "Unexpected error occurred while fetching blocked users",
        label: "Blocked users list API Error",
      });
    }
  },

  getUploadQuota: async (): Promise<UploadQuota> => {
    try {
      const response = await api.get<UploadQuota>("user/upload-quota");
      if (!response.data) {
        throw new Error("Empty response from upload quota API");
      }
      return response.data;
    } catch (error: unknown) {
      return handleApiError(error, {
        defaultMessage: "Unexpected error occurred while fetching upload quota",
        label: "Upload quota API Error",
      });
    }
  },
};
